import { Box, Container, Grid, Typography, IconButton, Divider, Stack } from "@mui/material";
import { Facebook, Instagram, Twitter, LinkedIn, YouTube } from "@mui/icons-material";
import { theme } from "./theme";
import useTranslationState from "./useTranslationState";
import { Link as RouterLink } from "react-router-dom";

function Footer3() {
    const translations = useTranslationState({
        geschaeftsfuehrer: '',
        geschaeftsfuehrerText: '',
        address: '',
        addressText: '',
        email: '',
        emailText: '',
        phone: '',
        phoneText: '',
        imprint: 'Imprint',
        privacyPolicy: 'Privacy Policy',
        termsOfService: 'Terms of Service',
        contact: 'Contact',
        followUs: 'Follow Us',
        allRightsReserved: '© 2023 Ukoni. All rights reserved.',
    });

    // Email and phone link styling
    const linkStyle = {
        color: 'rgba(255, 255, 255, 0.7)',
        textDecoration: 'none',
        display: 'block',
        marginTop: '0.5rem',
        fontSize: '0.875rem',
    };

    // Footer link styling
    const footerLinkStyle = {
        color: 'rgba(255, 255, 255, 0.7)',
        textDecoration: 'none',
        fontSize: '0.875rem',
    };

    return (
        <Box
            sx={{
                width: '100%',
                backgroundColor: theme.palette.primary.dark,
                color: 'white',
                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                py: 6,
            }}
        >
            <Container maxWidth="lg">
                <Grid container spacing={4}>
                    {/* Company Info */}
                    <Grid item xs={12} md={4}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                            }}
                        >
                            <img
                                src="/logo.png"
                                alt="Ukoni"
                                style={{
                                    height: '60px',
                                    width: 'auto',
                                    filter: 'invert(1)',
                                    marginBottom: '16px',
                                }}
                            />

                            {/* Social Media Icons */}
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    fontWeight: 500,
                                    mb: 1,
                                }}
                            >
                                {translations.followUs}
                            </Typography>
                            <Stack direction="row" spacing={1}>
                                <IconButton
                                    href="https://www.instagram.com/ukoni.hg/"
                                    size="small"
                                    sx={{
                                        color: 'white',
                                        border: '1px solid rgba(255,255,255,0.3)',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255,255,255,0.1)',
                                            borderColor: 'white',
                                        }
                                    }}
                                >
                                    <Instagram fontSize="small" />
                                </IconButton>
                            </Stack>
                        </Box>
                    </Grid>

                    {/* Contact Information */}
                    <Grid item xs={12} md={4}>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 500,
                                mb: 1,
                            }}
                        >
                            {translations.contact}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                mb: 1,
                            }}
                        >
                            Ukoni H&G UG
                        </Typography>
                        <Stack spacing={2}>
                            <Box>
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        fontWeight: 500,
                                        color: 'rgba(255, 255, 255, 0.9)'
                                    }}
                                >
                                    {translations.address}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'rgba(255, 255, 255, 0.7)',
                                        mt: 0.5,
                                    }}
                                >
                                    {translations.addressText}
                                </Typography>
                            </Box>

                            <Box>
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        fontWeight: 500,
                                        color: 'rgba(255, 255, 255, 0.9)'
                                    }}
                                >
                                    {translations.email}
                                </Typography>
                                <a
                                    href={`mailto:${translations.emailText}`}
                                    style={linkStyle}
                                    onMouseOver={(e) => e.currentTarget.style.color = 'white'}
                                    onMouseOut={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)'}
                                >
                                    {translations.emailText}
                                </a>
                            </Box>

                            <Box>
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        fontWeight: 500,
                                        color: 'rgba(255, 255, 255, 0.9)'
                                    }}
                                >
                                    {translations.phone}
                                </Typography>
                                <a
                                    href={`tel:${translations.phoneText}`}
                                    style={linkStyle}
                                    onMouseOver={(e) => e.currentTarget.style.color = 'white'}
                                    onMouseOut={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)'}
                                >
                                    {translations.phoneText}
                                </a>
                            </Box>
                        </Stack>
                    </Grid>

                    {/* Quick Links */}
                    <Grid item xs={12} md={4}>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 500,
                                mb: 3,
                            }}
                        >
                            {translations.geschaeftsfuehrer}
                        </Typography>
                        <Stack spacing={1.5}>
                            <RouterLink
                                to="/imprint"
                                style={footerLinkStyle}
                                onMouseOver={(e) => e.currentTarget.style.color = 'white'}
                                onMouseOut={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)'}
                            >
                                {translations.imprint}
                            </RouterLink>
                            <RouterLink
                                to="/privacy-policy"
                                style={footerLinkStyle}
                                onMouseOver={(e) => e.currentTarget.style.color = 'white'}
                                onMouseOut={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)'}
                            >
                                {translations.privacyPolicy}
                            </RouterLink>
                            <RouterLink
                                to="/terms"
                                style={footerLinkStyle}
                                onMouseOver={(e) => e.currentTarget.style.color = 'white'}
                                onMouseOut={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)'}
                            >
                                {translations.termsOfService}
                            </RouterLink>
                            <RouterLink
                                to="/#contact"
                                style={footerLinkStyle}
                                onMouseOver={(e) => e.currentTarget.style.color = 'white'}
                                onMouseOut={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)'}
                            >
                                {translations.contact}
                            </RouterLink>
                        </Stack>
                    </Grid>
                </Grid>

                <Divider sx={{ my: 4, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

                {/* Copyright */}
                <Typography
                    variant="body2"
                    sx={{
                        color: 'rgba(255, 255, 255, 0.6)',
                        textAlign: 'center',
                    }}
                >
                    {translations.allRightsReserved}
                </Typography>
            </Container>
        </Box>
    );
}

export default Footer3; 
